import { Box, Button, Chip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    PaloColumn,
    PaloFilterChips,
    PaloListView,
    PaloLockedSwitch,
    paloLockedHint,
    usePaloLockedRecords,
} from '@synergy/components-palo';
import { PoolAvatar, ValueOrNDash } from '@synergy/components';
import { ReactElement, useEffect } from 'react';
import { tokens } from '@synergy/theme-palo';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import urljoin from 'url-join';

import * as ContactListActions from '../contact-list/store/contact-list-actions';
import * as ContactListSelectors from '../contact-list/store/contact-list-selectors';
import { ContactListItem } from '../contact-list/models/contact-list-item';
import { PALO_LIST_AVATAR_STYLE } from '../../../../common/components/palo/palo-list-avatar';
import { PaloContactName } from '../../../../common/components/palo/palo-contact-card';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListSelection } from '../../../../common/components/palo/palo-list-selection';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloLockedCount } from '../../../../common/components/palo/use-palo-locked-count';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export const PaloContactList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('contacts');
    const columnFilter = usePaloColumnFilters('contacts');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloSort('lastName', 'asc');
    // Locked partners are out of the list unless they are asked for
    // (LST-S14); the switch that asks for them sits in the filter row.
    const locked = usePaloLockedRecords(params);
    const apiBaseUrl = useAppSelector(state => state.settings.apiBaseUrl);
    const imageBaseUrl = useAppSelector(state => state.settings.imageCachePaths?.['48x48']);
    const requesting = useAppSelector(ContactListSelectors.contactListRequestingSelector);
    const paginated = useAppSelector(ContactListSelectors.contactListItemsSelector);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);
    const imageBase = apiBaseUrl && imageBaseUrl ? urljoin(apiBaseUrl, imageBaseUrl) : undefined;

    useEffect(() => {
        dispatch(ContactListActions.fetchContactList(locked.params));
    }, [dispatch, locked.params]);

    const lockedMatches = usePaloLockedCount(
        '/contacts',
        locked.params,
        !locked.includeLocked && !requesting && 0 === paginated.items.length
    );

    // Same bar as on suppliers and members (#2212).
    const { selection, actions: selectionActions } = usePaloListSelection(
        paginated.items,
        id =>
            dispatch((_d, _g, { api }) => api.contactsAndOrganizations.Contact.ContactDetail.lock(id)).then(
                result => result.success
            ),
        id =>
            dispatch((_d, _g, { api }) => api.contactsAndOrganizations.Contact.ContactDetail.unlock(id)).then(
                result => result.success
            ),
        () => dispatch(ContactListActions.fetchContactList(locked.params))
    );

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const detailHref = (contact: ContactListItem): string => location.pathname + '/' + contact.id;

    const columns: PaloColumn<ContactListItem>[] = [
        {
            key: 'logo',
            type: 'icon',
            head: '',
            cell: contact => {
                const avatarFirstName = contact.firstName?.trim() || contact.fullName.trim();
                const avatarLastName = contact.firstName?.trim() ? contact.lastName : undefined;

                return (
                    <PoolAvatar
                        firstName={avatarFirstName}
                        lastName={avatarLastName}
                        style={PALO_LIST_AVATAR_STYLE}
                        src={contact.image && imageBase && urljoin(imageBase, contact.image)}
                        styleType="cover"
                    />
                );
            },
        },
        {
            key: 'number',
            link: true,
            type: 'documentNumber',
            sortKey: 'number',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: contact => contact.id,
        },
        {
            key: 'lastName',
            type: 'name',
            sortKey: 'lastName',
            head: <FormattedMessage id="app.table.column.lastName" />,
            cell: contact => <PaloContactName person={contact}>{contact.lastName}</PaloContactName>,
        },
        {
            key: 'firstName',
            type: 'name',
            sortKey: 'firstName',
            head: <FormattedMessage id="app.table.column.firstName" />,
            cell: contact => contact.firstName,
        },
        {
            // Same pill, same two words as the supplier and member lists: the
            // state of a record is read in the same place everywhere (#2212).
            key: 'status',
            sortKey: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: contact => (
                <Chip
                    size="small"
                    color={contact.isLocked ? 'error' : 'success'}
                    label={<FormattedMessage id={contact.isLocked ? 'app.status.locked' : 'app.status.active'} />}
                />
            ),
        },
        {
            key: 'email',
            type: 'name',
            sortKey: 'email',
            head: <FormattedMessage id="app.table.column.email" />,
            cell: contact => contact.email,
        },
        {
            key: 'phone',
            type: 'phone',
            head: <FormattedMessage id="app.table.column.phone" />,
            cell: contact => <ValueOrNDash value={contact.phone} />,
        },
        {
            key: 'mainAccount',
            type: 'name',
            sortKey: 'mainAccount',
            head: <FormattedMessage id="app.table.column.mainOrganization" />,
            cell: contact => <ValueOrNDash value={contact.mainAccount?.name} />,
        },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(ROUTES.contactsAndOrganizations.contacts.new)}
        >
            <FormattedMessage id="app.contactsAndOrganizations.contact.newShort" />
        </Button>
    );

    return (
        <PaloListView<ContactListItem>
            filterChips={<PaloFilterChips />}
            filterToggle={<PaloLockedSwitch checked={locked.includeLocked} onChange={locked.setIncludeLocked} />}
            emptyHint={
                lockedMatches > 0 ? (
                    <Box
                        component="span"
                        role="button"
                        onClick={() => locked.setIncludeLocked(true)}
                        sx={{ color: tokens.color.accent, cursor: 'pointer', textDecoration: 'underline' }}
                    >
                        {paloLockedHint(lockedMatches)}
                    </Box>
                ) : undefined
            }
            selection={selection}
            selectionActions={selectionActions}
            title={<FormattedMessage id="app.contactsAndOrganizations.contact.list.title" />}
            pageTitle={intl.formatMessage({ id: 'app.contactsAndOrganizations.contact.list.title' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            searchValue={searchParams.get('search') ?? ''}
            onSearchChange={value => {
                const next = new URLSearchParams(searchParams);
                if (value) {
                    next.set('search', value);
                } else {
                    next.delete('search');
                }
                next.set('page', '1');
                setSearchParams(next, { replace: true });
            }}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="contacts" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={contact => contact.id}
            requesting={requesting}
            emptyLabel="Keine Personen."
            rowHref={detailHref}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        />
    );
};
